import React, { useEffect, useState } from "react";
import axios from "axios";
import { useT } from "../i18n/I18nProvider";

const LOGO = "/elsol-logo.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function VisitorCounter() {
  const [count, setCount] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const already = sessionStorage.getItem("el-sol-visited");
        const url = `${API}/visits`;
        const res = already
          ? await axios.get(url)
          : await axios.post(url);
        if (!already) sessionStorage.setItem("el-sol-visited", "1");
        if (!cancelled) setCount(res.data?.count ?? null);
      } catch (e) {
        if (!cancelled) setCount(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  if (count === null) return null;
  const formatted = new Intl.NumberFormat("en-US").format(count);
  const padded = String(formatted).padStart(6, "0");
  return (
    <div
      data-testid="visitor-counter"
      className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-stone-300"
      title={`${formatted} total visits`}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="text-orange-300" aria-hidden="true">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>
      <span className="font-bold text-orange-300">Visitors</span>
      <span data-testid="visitor-count" className="font-mono tracking-widest text-amber-100 bg-emerald-950/70 border border-emerald-800 rounded px-2 py-0.5">
        {padded}
      </span>
    </div>
  );
}

export default function Footer() {
  const { t } = useT();
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0f3d21] text-stone-200 pt-16 pb-10 relative"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="bg-white/95 inline-block rounded-2xl px-4 py-3 shadow-lg">
            <img src={LOGO} alt="El Sol Mexican Imports" className="h-20 w-auto object-contain" />
          </div>
          <p className="mt-5 text-[14px] text-stone-300 leading-relaxed max-w-sm">
            {t("footer.tagline")}
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="text-[11px] uppercase tracking-[0.22em] text-orange-300 font-bold mb-4">{t("footer.visit")}</div>
          <div data-testid="footer-address" className="text-[14px] text-stone-200 leading-relaxed">
            5609 E Watauga Rd<br/>
            Watauga, TX 76148
          </div>
          <a
            href="https://www.google.com/maps/search/?api=1&query=5609+E+Watauga+Rd+Watauga+TX+76148"
            target="_blank"
            rel="noreferrer"
            data-testid="footer-directions-link"
            className="inline-flex items-center gap-1.5 mt-3 text-[13px] font-bold text-orange-300 hover:text-orange-200 transition-colors"
          >
            {t("footer.directions")}
          </a>
        </div>

        <div className="md:col-span-2">
          <div className="text-[11px] uppercase tracking-[0.22em] text-orange-300 font-bold mb-4">{t("footer.contact")}</div>
          <div className="space-y-2 text-[14px]">
            <a href="tel:+18176561046" data-testid="footer-phone" className="block text-stone-200 hover:text-orange-200 transition-colors">(817) 656-1046</a>
            <a href="mailto:elsolraul@yahoo.com" data-testid="footer-email" className="block text-stone-200 hover:text-orange-200 transition-colors break-all">elsolraul@yahoo.com</a>
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="text-[11px] uppercase tracking-[0.22em] text-orange-300 font-bold mb-4">{t("footer.follow")}</div>
          <a
            href="https://www.facebook.com/p/El-Sol-Mexican-Imports-100063473202830/"
            target="_blank"
            rel="noreferrer"
            data-testid="footer-facebook"
            className="inline-flex items-center gap-2 text-[14px] text-stone-200 hover:text-orange-200 transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12a10 10 0 1 0-11.55 9.88v-6.99H7.9V12h2.55V9.79c0-2.52 1.5-3.91 3.79-3.91 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.78-1.63 1.57V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0 0 22 12z"/></svg>
            {t("footer.facebook")}
          </a>
          <a
            href="https://www.instagram.com/popular/el-sol-mexican-imports/"
            target="_blank"
            rel="noreferrer"
            data-testid="footer-instagram"
            className="inline-flex items-center gap-2 mt-3 text-[14px] text-stone-200 hover:text-orange-200 transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
            </svg>
            Instagram
          </a>
          <a
            href="https://www.pinterest.com/elsolraul/talavera-tiles/"
            target="_blank"
            rel="noreferrer"
            data-testid="footer-pinterest"
            className="inline-flex items-center gap-2 mt-3 text-[14px] text-stone-200 hover:text-orange-200 transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 2C6.48 2 2 6.48 2 12c0 4.21 2.6 7.81 6.29 9.27-.09-.79-.17-2 .04-2.86.19-.78 1.22-4.94 1.22-4.94s-.31-.62-.31-1.54c0-1.45.84-2.53 1.88-2.53.89 0 1.32.67 1.32 1.46 0 .89-.57 2.22-.86 3.46-.24 1.04.52 1.88 1.54 1.88 1.85 0 3.27-1.95 3.27-4.76 0-2.49-1.79-4.23-4.34-4.23-2.96 0-4.7 2.22-4.7 4.51 0 .89.34 1.85.77 2.37.08.1.1.19.07.29-.08.34-.27 1.04-.31 1.18-.05.2-.16.24-.37.14-1.36-.63-2.21-2.62-2.21-4.22 0-3.43 2.49-6.58 7.18-6.58 3.77 0 6.7 2.69 6.7 6.28 0 3.75-2.36 6.76-5.64 6.76-1.1 0-2.14-.57-2.49-1.25 0 0-.55 2.07-.68 2.58-.25.95-.91 2.13-1.36 2.86.99.32 2.04.5 3.13.5 5.52 0 10-4.48 10-10S17.52 2 12 2z"/>
            </svg>
            Pinterest
          </a>
          <a
            href="https://www.yelp.com/biz/el-sol-mexican-imports-wholesale-watauga"
            target="_blank"
            rel="noreferrer"
            data-testid="footer-yelp"
            className="inline-flex items-center gap-2 mt-3 text-[14px] text-stone-200 hover:text-orange-200 transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M14.79 14.34l3.34 1.1a.92.92 0 0 1 .55 1.43l-2.07 3.18a.93.93 0 0 1-1.62-.07l-2.06-3.45a.93.93 0 0 1 .94-1.39zm-2.27-2.49l-1.31-9.74A.93.93 0 0 1 12.4 1c2.09.49 4 1.5 5.55 2.94a.93.93 0 0 1 .04 1.36l-4.42 7.04a.93.93 0 0 1-1.05-.49zm-2.21.86l-3.18-2.06a.93.93 0 0 1-.07-1.62l3.18-2.07a.93.93 0 0 1 1.43.55l1.1 3.34a.93.93 0 0 1-1.39.94l-1.07-1.08zm.78 3.42l-1.1 3.34a.93.93 0 0 1-1.43.55L5.39 17.95a.93.93 0 0 1 .07-1.62l3.45-2.06a.93.93 0 0 1 1.18 1.86zm-1.85-2.71L5 14.06a.93.93 0 0 1-1.18-.94c.13-1.6.62-3.13 1.43-4.51a.93.93 0 0 1 1.36-.27l3.18 2.07a.93.93 0 0 1-.55 1.71z"/>
            </svg>
            Yelp
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-10 mt-14 pt-6 border-t border-emerald-900/60 flex flex-col md:flex-row gap-3 justify-between items-start md:items-center text-[12px] text-stone-400">
        <div>© {new Date().getFullYear()} El Sol Mexican Imports. {t("footer.copyright")}</div>
        <VisitorCounter />
        <div className="font-script text-orange-300 text-lg leading-none">{t("footer.thanks")}</div>
      </div>
    </footer>
  );
}
